/**
 * App Component
 * 메인 라우팅 설정
 *
 * Phase 7: IP 화이트리스트 보안 정책 적용
 * - 앱 초기화 시 /checkIP API를 호출하여 IP 검증
 * - 허용된 IP: 59.13.192.250, 59.12.234.2
 * - 허용되지 않은 IP는 RestrictedAccessPage 표시
 *
 * V37: 온보딩 플로우 추가
 * - 약관 동의 → 비밀번호 변경 → 메인 진입
 */

import React, { useEffect, useState, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Loader } from 'lucide-react';
import { AppShell } from './shared/components/AppShell';
import { ProtectedRoute as OldProtectedRoute } from './shared/components/ProtectedRoute';
import { ProtectedRoute } from './routes/ProtectedRoute';  // RBAC 기반 Route Guard
import { OrgManagementRouteGuard } from './routes/OrgManagementRouteGuard'; // V136: 직책 레벨 라우트 가드
import { OrgManagementAccessGuard } from './routes/OrgManagementAccessGuard'; // V139: 조직관리 접근 가드
// V91.3: ProjectTagsAccessGuard 제거 (태그 마스터가 프로젝트 관리 내부 탭으로 이동)
import { MmAccessGuard } from './routes/MmAccessGuard'; // V60: MM관리 접근 가드
import { NonExecGuard } from './routes/NonExecGuard'; // V202: role_exec 접근 차단 가드
import { OnboardingGuard } from './shared/components/OnboardingGuard'; // V37: 온보딩 가드
import { TerminatedUserGuard } from './shared/components/TerminatedUserGuard'; // V72: 퇴사자 접근 차단
import { LoginPage } from './pages/LoginPage';
import { NoticePage } from './pages/NoticePage';
// V82.7 (rollback): MFA 제거 — 외부망은 RestrictedAccessPage 표시 (Phase 1 이전 동작 복원)
import { RestrictedAccessPage } from './pages/common/RestrictedAccessPage';
import { checkClientIP } from './utils/ipCheck';

// V37: 온보딩 페이지
import { TermsPage } from './pages/auth/TermsPage';
import { ChangePasswordPage } from './pages/auth/ChangePasswordPage';
// V82.7 (rollback): MFA 페이지/Guard 제거

// 근로관리 페이지
import { WorkMyDashboardPage } from './pages/work/WorkMyDashboardPage';
import { WorkSchedulePage } from './pages/work/WorkSchedulePage';
import { WorkMyApprovalsPage } from './pages/work/WorkMyApprovalsPage';
import { WorkApprovalsPage } from './pages/work/WorkApprovalsPage';

// 인사관리 페이지
import { HrEmployeesPage } from './pages/hr/HrEmployeesPage';
import { EmployeeDetailPageV19 } from './pages/hr/EmployeeDetailPageV19';
import { HrEmploymentStatusPage } from './pages/hr/HrEmploymentStatusPage';
import { HrOrgPage } from './pages/hr/HrOrgPage';
import { HrOrgUnitsPage } from './pages/hr/HrOrgUnitsPage';
import { HrPositionsPage } from './pages/hr/HrPositionsPage';
import { HrJobTitlesPage } from './pages/hr/HrJobTitlesPage';
import { HrPersonnelAppointmentPage } from './pages/hr/HrPersonnelAppointmentPage';
import { HrTransfersPage } from './pages/hr/HrTransfersPage';
import { HrDashboardPage } from './pages/hr/HrDashboardPage';
import { HrLeaveSettingsPage } from './pages/hr/HrLeaveSettingsPage';
import { HrHolidaysPage } from './pages/hr/HrHolidaysPage';
import { HrPerformancePage } from './pages/hr/HrPerformancePage';
import HrEvaluationTemplatesPage from './pages/hr/HrEvaluationTemplatesPage'; // V57.2
import { HrInterviewDetailPage } from './pages/hr/HrInterviewDetailPage'; // V55.64: HR 면담 상세
import { HrLeaveOverviewPage } from './pages/hr/HrLeaveOverviewPage';
import { HrLeaveApprovalsPage } from './pages/hr/HrLeaveApprovalsPage'; // V50: HR 휴가 승인 관리
import { HrEmploymentManagementPage } from './pages/hr/HrEmploymentManagementPage';
import { HrTerminatedEmployeesPage } from './pages/hr/HrTerminatedEmployeesPage'; // V77: 퇴사자 관리
import { HrLeaveOfAbsencesPage } from './pages/hr/HrLeaveOfAbsencesPage'; // V67: 휴직 현황

// 개인 휴가 페이지
import { MyLeavePage } from './pages/my/MyLeavePage';
import { MyProfilePage } from './pages/my/MyProfilePage'; // V62

// V55.35: 내 면담기록 페이지
import { MyInterviewsPage } from './pages/my/MyInterviewsPage';
// V55.37: 내 면담기록 상세 페이지
import { MyInterviewDetailPage } from './pages/my/MyInterviewDetailPage';

// V135: 조직관리 페이지 (팀관리 → 조직관리 개편)
// V136: 조직원면담 페이지 추가
// V5.18: 조직 휴가 관리 페이지 추가 (OrgLeavePage 플레이스홀더 삭제)
// V91.3: OrgProjectTagsPage 제거 (태그 마스터가 프로젝트 관리 내부 탭으로 이동)
// V55.30: 면담기록 작성 페이지 추가
import { OrgMembersPage, OrgProjectsPage, OrgWorkStatusPage, OrgInterviewsPage, InterviewWritePage, InterviewManagementPage, OrgEvaluationsPage, OrgTransferPage } from './pages/org-management';
import { OrgWorkforceManagementPage } from './pages/org-management/OrgWorkforceManagementPage';
import { WorkforceAccessGuard } from './routes/WorkforceAccessGuard';
import { OrgLeaveManagementPage } from './pages/org-management/OrgLeaveManagementPage';
// V66: 조직 휴가 캘린더
import { OrgLeaveCalendarPage } from './pages/org-management/OrgLeaveCalendarPage';

// MM관리 페이지
import { MMOverviewPage } from './pages/mm/MMOverviewPage';
import { MMRegisterPage } from './pages/mm/MMRegisterPage';
import { MMMasterPage } from './pages/mm/MMMasterPage'; // V113: MM 마스터 설정

// 조직도 페이지
import { OrgChartPage } from './pages/organization/OrgChartPage';
import { SeatMapPage } from './pages/organization/SeatMapPage'; // V52: 좌석배치도

// Admin 페이지
import { AdminRolesPage } from './pages/admin/AdminRolesPage';
import { AdminCleanupPage } from './pages/admin/AdminCleanupPage'; // V46: 승인요청 테스트 데이터 정리
import { AdminTestUtilitiesPage } from './pages/admin/AdminTestUtilitiesPage'; // V47.15: 휴가 데이터 테스트 유틸리티
import { IpWhitelistPage } from './pages/admin/IpWhitelistPage'; // V82.7 Phase 1: IP 화이트리스트 관리
import { AdminNotificationSettingsPage } from './pages/admin/AdminNotificationSettingsPage'; // V67: 알림 마스터
// V70.27: 인력 현황 (조직관리) + 인력 현황 관리 (Admin)
import { OrgWorkforceOverviewPage } from './pages/org-management/OrgWorkforceOverviewPage';
import { AdminWorkforceOverviewSettingsPage } from './pages/admin/AdminWorkforceOverviewSettingsPage';
import { WorkforceOverviewAccessGuard } from './routes/WorkforceOverviewAccessGuard';
// V70-S: 감사 대응 인사관리 (전사 근로 현황 / 연장 근무 관리)
import { HrKrAuditAccessGuard } from './routes/HrKrAuditAccessGuard';
import { HrCompanyWorkStatusPage } from './pages/hr/HrCompanyWorkStatusPage';
import { HrOvertimeManagementPage } from './pages/hr/HrOvertimeManagementPage';
// V70 Tournament: 사용자 측 3 페이지 + 관리자 설정 + Guard
import { TournamentDashboardPage } from './pages/tournament/TournamentDashboardPage';
import { BracketDetailPage } from './pages/tournament/bracket/BracketDetailPage';
import { TournamentApplyPage } from './pages/tournament/TournamentApplyPage';
import { TournamentGameCenterPage } from './pages/tournament/TournamentGameCenterPage';
import { AcidRainGamePage } from './pages/tournament/AcidRainGamePage';
// V70G6B — 엔글 오목 (실시간 1:1 대전)
import { OmokLobbyPage } from './pages/tournament/omok/OmokLobbyPage';
import { OmokRoomPage } from './pages/tournament/omok/OmokRoomPage';
import { AcidRainLeaderboardPage } from './pages/tournament/AcidRainLeaderboardPage';
import { MarbleGamePage } from './pages/tournament/marble/MarbleGamePage';
import { MarbleLeaderboardPage } from './pages/tournament/marble/MarbleLeaderboardPage';
import { OpenPeriodGuard } from './pages/tournament/shared/OpenPeriodGuard';
import { acidRainApi } from './pages/tournament/acidRain/serverApi';
import { marbleApi } from './pages/tournament/marble/mock/serverApi';
import { TournamentAdminSettingsPage } from './pages/admin/TournamentAdminSettingsPage';
import { TournamentApplyAdminPage } from './pages/admin/TournamentApplyAdminPage';
import { TournamentAccessGuard } from './routes/TournamentAccessGuard';

// V83: 공지사항 페이지
import { AnnouncementsPage, AnnouncementDetailPage, AnnouncementFormPage } from './pages/announcements';

// V227: 디버그 패널 (개발환경 전용)
import DebugPanelPage from './pages/debug/DebugPanelPage';

// V250: 증명서 발급
import { MyCertificatesPage } from './pages/certificates/MyCertificatesPage';
import { HrCertificateManagementPage } from './pages/hr/HrCertificateManagementPage';

// V57: 평가
import { MyEvaluationsPage } from './pages/my/MyEvaluationsPage';

// V70 엔글 룰렛 Phase 1 — lazy chunk (matter-js + pixi.js 번들 분리, 메인 번들 영향 無)
const RouletteDemoPage = React.lazy(() => import('./pages/tournament/roulette/RouletteDemoPage'));
// V70 엔글 룰렛 V2P — Static Asset 연결 (public/game-v2/* 로 redirect)
const RouletteV2Redirect = React.lazy(() => import('./pages/tournament/roulette-v2/RouletteV2Redirect'));
// V70 엔글 룰렛 V3 — Static Asset 연결 (public/game-v3/* 로 redirect, 메뉴 미노출)
const RouletteV3Redirect = React.lazy(() => import('./pages/tournament/roulette-v3/RouletteV3Redirect'));
// V70 엔글 룰렛 — 다중 경품 추첨 플랫폼 (서바이벌 / 잭팟 / 룰렛 / 미스터리박스)
const RouletteMainPage = React.lazy(() => import('./pages/tournament/roulette/RouletteMainPage'));

function App() {
  // Phase 7: IP 화이트리스트 검증 상태
  const [ipCheckStatus, setIPCheckStatus] = useState<'loading' | 'allowed' | 'denied'>('loading');

  // Phase 7: 앱 초기화 시 IP 검증
  // V82.7 Phase 2: success 대신 allowed/networkType 명시 사용
  useEffect(() => {
    const verifyIP = async () => {
      const result = await checkClientIP();
      const isAllowed = result.allowed ?? result.success;
      const networkType = result.networkType ?? (isAllowed ? 'internal' : 'external');
      console.log('[App ipCheck]', {
        isAllowed,
        networkType,
        requiresMfa: result.requiresMfa,
        clientIP: result.clientIP,
      });
      if (isAllowed) {
        setIPCheckStatus('allowed');
      } else {
        console.info(
          `[App] IP 외부망 감지 (networkType=${networkType}, requiresMfa=${result.requiresMfa ?? true}). LoginPage + MFA 플로우로 진입 허용.`
        );
        setIPCheckStatus('denied');
      }
    };

    verifyIP();
  }, []);

  // Phase 7: IP 검증 중 - 로딩 UI 표시
  if (ipCheckStatus === 'loading') {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        gap: '16px',
        backgroundColor: '#f9fafb',
      }}>
        <Loader
          size={48}
          style={{
            color: '#3b82f6',
            animation: 'spin 1s linear infinite',
          }}
        />
        <p style={{
          fontSize: '16px',
          color: '#6b7280',
        }}>
          접속 권한을 확인하는 중...
        </p>
      </div>
    );
  }

  // V82.7 (rollback): 외부망 → RestrictedAccessPage (MFA 없음, Phase 1 이전 동작)
  if (ipCheckStatus === 'denied') {
    return <RestrictedAccessPage />;
  }

  return (
    <BrowserRouter>
      <Routes>
        {/* 로그인 페이지 (인증 불필요, 외부망도 진입 허용) */}
        <Route path="/login" element={<LoginPage />} />

        {/* V227: 디버그 패널 (개발환경 전용) */}
        <Route path="/debug" element={<DebugPanelPage />} />

        {/* V70 엔글 룰렛 — QA 샌드박스 라우트 (Playwright/비로그인 UI 검증 전용).
              본 라우트는 사이드바에 노출되지 않고, 샘플 synthetic 참가자로 UI/연출만 검증.
              실제 ERP 추첨은 반드시 /tournament/roulette (가드 + 로그인 보호) 사용.
              실 Firestore 호출 없음 — testPool 쿼리로 synthetic Employee 주입만.
              hostname QA/localhost 외에서는 컴포넌트 내부에서 거부됨 (sandboxHostBlocked). */}
        <Route
          path="/tournament/roulette-qa-sandbox"
          element={
            <Suspense fallback={<div style={{ padding: 24 }}>로딩 중…</div>}>
              <RouletteMainPage />
            </Suspense>
          }
        />

        {/* V37: 온보딩 페이지 (인증 필요, 레이아웃 없음) */}
        <Route
          path="/auth/terms"
          element={
            <OldProtectedRoute>
              <TermsPage />
            </OldProtectedRoute>
          }
        />
        <Route
          path="/auth/change-password"
          element={
            <OldProtectedRoute>
              <ChangePasswordPage />
            </OldProtectedRoute>
          }
        />

        {/* V82.7 (rollback): MFA enroll/challenge 라우트 제거 */}

        {/* 보호된 라우트들 (AppShell 레이아웃 적용 + 온보딩 가드 + V72 퇴사자 차단) */}
        {/* V82.7 (rollback): ExternalAccessMfaGuard 제거 — 외부망 차단은 App 상단에서 처리 */}
        <Route
          path="/"
          element={
            <OldProtectedRoute>
              <OnboardingGuard>
                <TerminatedUserGuard>
                  <AppShell />
                </TerminatedUserGuard>
              </OnboardingGuard>
            </OldProtectedRoute>
          }
        >
          {/* V55.23: 기본 경로를 /announcements로 리다이렉트 (대시보드 제거) */}
          <Route index element={<Navigate to="/announcements" replace />} />

          {/* 공지사항 (V83: 신규 공지사항 시스템) */}
          <Route path="notice" element={<Navigate to="/announcements" replace />} />
          <Route path="announcements" element={<AnnouncementsPage />} />
          <Route path="announcements/new" element={<AnnouncementFormPage />} />
          <Route path="announcements/:id" element={<AnnouncementDetailPage />} />
          <Route path="announcements/:id/edit" element={<AnnouncementFormPage />} />

          {/* 조직도 */}
          <Route path="organization" element={<OrgChartPage />} />
          {/* V52: 좌석배치도 */}
          <Route path="organization/seat-map" element={<SeatMapPage />} />

          {/* 근로관리 */}
          <Route path="work/my-dashboard" element={<WorkMyDashboardPage />} />
          <Route path="work/schedule" element={<WorkSchedulePage />} />
          {/* V151: 내 연차현황 삭제 - 내 휴가관리로 리다이렉트 */}
          <Route path="work/my-leaves" element={<Navigate to="/my/leave" replace />} />
          {/* V66: 조직 휴가 캘린더 (근로관리 하위, 전 직원 접근, BE 본인 조직 SELF 스코프) */}
          <Route path="work/leave-calendar" element={<OrgLeaveCalendarPage />} />
          <Route path="work/my-approvals" element={<WorkMyApprovalsPage />} />
          {/*
            V168: 승인요청 관리 라우트에 Route Guard 적용
            - work.approve 권한이 있는 사용자 접근 가능 (RBAC 기반)
            - 조직관리에서 승인자로 지정된 사용자도 접근 가능 (isOrgApprover)
            - 접근 조건: (RBAC work.approve) OR (isOrgApprover)
          */}
          <Route
            path="work/approvals"
            element={
              <ProtectedRoute path="/work/approvals">
                <WorkApprovalsPage />
              </ProtectedRoute>
            }
          />

          {/* 개인 휴가 */}
          <Route path="my/leave" element={<MyLeavePage />} />

          {/* V55.35: 내 면담기록 */}
          {/* V202: role_exec 접근 차단 */}
          <Route path="my/interviews" element={<NonExecGuard><MyInterviewsPage /></NonExecGuard>} />
          {/* V55.37: 내 면담기록 상세 */}
          <Route path="my/interviews/:interviewId" element={<NonExecGuard><MyInterviewDetailPage /></NonExecGuard>} />

          {/* V57: 내 평가기록 */}
          <Route path="my/evaluations" element={<NonExecGuard><MyEvaluationsPage /></NonExecGuard>} />

          {/* V62: 내정보 */}
          <Route path="my/profile" element={<MyProfilePage />} />

          {/* 인사관리 */}
          {/*
            직원관리 라우트에 RBAC 기반 Route Guard 적용
            - hr.manage 권한이 있는 사용자만 접근 가능
            - 권한 없는 사용자는 ForbiddenPage로 이동
            - Sidebar 메뉴 필터링과 함께 이중 방어 구조
          */}
          <Route
            path="hr/employees"
            element={
              <ProtectedRoute path="/hr/employees">
                <HrEmployeesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="hr/employees/:employeeId"
            element={
              <ProtectedRoute path="/hr/employees">
                <EmployeeDetailPageV19 />
              </ProtectedRoute>
            }
          />
          <Route path="hr/employment-status" element={<HrEmploymentStatusPage />} />
          <Route path="hr/org" element={<HrOrgPage />} />
          {/*
            조직관리 라우트에 RBAC 기반 Route Guard 적용 (Chapter 4-1)
            - hr.manage 권한이 있는 사용자만 접근 가능 (직원관리와 동일)
            - 권한 없는 사용자는 ForbiddenPage로 이동
            - Sidebar 메뉴 필터링과 함께 이중 방어 구조
          */}
          <Route
            path="hr/org-units"
            element={
              <ProtectedRoute path="/hr/org-units">
                <HrOrgUnitsPage />
              </ProtectedRoute>
            }
          />
          {/*
            직위 관리 라우트에 RBAC 기반 Route Guard 적용 (Chapter 5-1)
            - hr.manage 권한이 있는 사용자만 접근 가능 (직원관리, 조직관리와 동일)
            - 직위(호칭) 마스터 데이터 관리
            - Sidebar 메뉴 필터링과 함께 이중 방어 구조
          */}
          <Route
            path="hr/positions"
            element={
              <ProtectedRoute path="/hr/positions">
                <HrPositionsPage />
              </ProtectedRoute>
            }
          />
          {/*
            직책 관리 라우트에 RBAC 기반 Route Guard 적용 (Chapter 6-1)
            - hr.manage 권한이 있는 사용자만 접근 가능 (HR, Super Admin)
            - 직책(Job Title) 마스터 데이터 관리
            - 팀원, 담당, 팀장, 실장, 본부장, 대표
          */}
          <Route
            path="hr/job-titles"
            element={
              <ProtectedRoute path="/hr/job-titles">
                <HrJobTitlesPage />
              </ProtectedRoute>
            }
          />
          {/*
            V51: 인사발령 페이지 (4탭 구조)
            - hr.manage 권한이 있는 사용자만 접근 가능 (HR, Super Admin)
            - 탭 1: 진급후보 산출
            - 탭 2: 임명 (추후 구현)
            - 탭 3: 인사발령 예약 (추후 구현)
            - 탭 4: 히스토리 (추후 구현)
          */}
          <Route
            path="hr/personnel-appointment"
            element={
              <ProtectedRoute path="/hr/personnel-appointment">
                <HrPersonnelAppointmentPage />
              </ProtectedRoute>
            }
          />
          {/* V51: 기존 진급 후보 URL은 인사발령 페이지로 리다이렉트 */}
          <Route
            path="hr/promotion-candidates"
            element={<Navigate to="/hr/personnel-appointment" replace />}
          />
          {/* V52: HR 설정 메뉴 제거 - 인사발령 페이지로 리다이렉트 */}
          <Route
            path="hr/settings"
            element={<Navigate to="/hr/personnel-appointment" replace />}
          />
          {/*
            전배 요청 관리 라우트에 RBAC 기반 Route Guard 적용 (Chapter 6-2)
            - hr.manage 권한이 있는 사용자만 접근 가능 (HR, Super Admin)
            - 조직 간 직원 이동 요청 및 승인 프로세스 관리
            - Sidebar 메뉴 필터링과 함께 이중 방어 구조
          */}
          <Route
            path="hr/transfers"
            element={
              <ProtectedRoute path="/hr/transfers">
                <HrTransfersPage />
              </ProtectedRoute>
            }
          />
          {/*
            HR 대시보드 라우트에 RBAC 기반 Route Guard 적용 (Chapter 6-2)
            - hr.manage 권한이 있는 사용자만 접근 가능 (HR, Super Admin)
            - 인사 관련 주요 지표와 통계 제공
            - Sidebar 메뉴 필터링과 함께 이중 방어 구조
          */}
          <Route
            path="hr/dashboard"
            element={
              <ProtectedRoute path="/hr/dashboard">
                <HrDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route path="hr/leave-settings" element={<HrLeaveSettingsPage />} />
          {/* V234: ProtectedRoute 추가 — getHolidays(checkIsAdminLike) 마운트 호출 차단 */}
          <Route
            path="hr/holidays"
            element={
              <ProtectedRoute path="/hr/holidays">
                <HrHolidaysPage />
              </ProtectedRoute>
            }
          />
          {/* V234: ProtectedRoute 추가 — listHrInterviewShares(checkIsAdminLike) 마운트 호출 차단 */}
          <Route
            path="hr/performance"
            element={
              <ProtectedRoute path="/hr/performance">
                <HrPerformancePage />
              </ProtectedRoute>
            }
          />
          {/* V57.2: 평가서 관리 (폼빌더) */}
          <Route
            path="hr/evaluation-templates"
            element={
              <ProtectedRoute path="/hr/evaluation-templates">
                <HrEvaluationTemplatesPage />
              </ProtectedRoute>
            }
          />
          {/* V55.64: HR 면담 상세 (읽기 전용) */}
          <Route path="hr/performance/interviews/:shareId" element={<HrInterviewDetailPage />} />
          {/*
            HR 휴가 현황 라우트에 RBAC 기반 Route Guard 적용 (Phase 5)
            - hr.manage 권한이 있는 사용자만 접근 가능 (HR, Super Admin)
            - 전체 직원의 휴가 잔액 및 사용 현황 조회
            - Sidebar 메뉴 필터링과 함께 이중 방어 구조
          */}
          <Route
            path="hr/leave-overview"
            element={
              <ProtectedRoute path="/hr/leave-overview">
                <HrLeaveOverviewPage />
              </ProtectedRoute>
            }
          />
          {/*
            V67: 휴직 현황 라우트 (휴가 현황 페이지의 "휴직 현황 보기" 버튼 → 이동)
            - hr.view 권한 (HrLeaveOverviewPage 와 동일 ProtectedRoute path 재사용)
            - leaveOfAbsences.isCurrent SSoT 기준 BE callable 단독 검증
          */}
          <Route
            path="hr/leave-of-absences"
            element={
              <ProtectedRoute path="/hr/leave-overview">
                <HrLeaveOfAbsencesPage />
              </ProtectedRoute>
            }
          />
          {/*
            V50: HR 휴가 승인 관리 라우트
            - hr.view 권한이 있는 사용자만 접근 가능 (HR, Super Admin)
            - orgAndHr 플로우에서 조직장 승인 완료된 휴가 요청을 최종 승인
          */}
          <Route
            path="hr/leave-approvals"
            element={
              <ProtectedRoute path="/hr/leave-approvals">
                <HrLeaveApprovalsPage />
              </ProtectedRoute>
            }
          />
          {/*
            V45: 고용관리 라우트
            - hr.employee.read 권한이 있는 사용자만 접근 가능
            - 퇴사 예약, 계약 연장, 고용형태 변경 등 관리
          */}
          <Route
            path="hr/employment-management"
            element={
              <ProtectedRoute path="/hr/employment-management">
                <HrEmploymentManagementPage />
              </ProtectedRoute>
            }
          />
          {/*
            V77: 퇴사자 관리 라우트
            - hr.view 권한이 있는 사용자만 접근 가능 (HR, Super Admin)
            - 퇴사 당시 정보 스냅샷 기반 조회
            - 증명서 발급 기능 (추후 구현)
          */}
          <Route
            path="hr/terminated-employees"
            element={
              <ProtectedRoute path="/hr/terminated-employees">
                <HrTerminatedEmployeesPage />
              </ProtectedRoute>
            }
          />

          {/* V250: 증명서 발급 (직원) */}
          <Route
            path="certificates"
            element={
              <ProtectedRoute path="/certificates">
                <MyCertificatesPage />
              </ProtectedRoute>
            }
          />
          {/* V70-S: 감사 대응 — 전사 근로 현황 / 연장 근무 관리 (KR 전사원)
              ProtectedRoute('hr.manage') + HrKrAuditAccessGuard(system_admin·role_admin_kr·KR role_hr). 서버도 동일 규칙 재검증 */}
          <Route
            path="hr/company-work-status"
            element={
              <ProtectedRoute path="/hr/company-work-status">
                <HrKrAuditAccessGuard>
                  <HrCompanyWorkStatusPage />
                </HrKrAuditAccessGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="hr/overtime-management"
            element={
              <ProtectedRoute path="/hr/overtime-management">
                <HrKrAuditAccessGuard>
                  <HrOvertimeManagementPage />
                </HrKrAuditAccessGuard>
              </ProtectedRoute>
            }
          />
          {/* V250: 증명서 관리 (HR) */}
          <Route
            path="hr/certificate-management"
            element={
              <ProtectedRoute path="/hr/certificate-management">
                <HrCertificateManagementPage />
              </ProtectedRoute>
            }
          />

          {/* V135: 조직관리 (팀관리 → 조직관리 개편) */}
          {/* V136: 직책 레벨 정책 적용 - 실장까지만 접근 가능한 메뉴에 OrgManagementRouteGuard 적용 */}
          {/* V139: 모든 org-management 경로에 OrgManagementAccessGuard 적용 (TE 접근 차단) */}
          <Route
            path="org-management/members"
            element={
              <OrgManagementAccessGuard>
                <OrgMembersPage />
              </OrgManagementAccessGuard>
            }
          />
          <Route
            path="org-management/projects"
            element={
              <OrgManagementAccessGuard>
                <OrgProjectsPage />
              </OrgManagementAccessGuard>
            }
          />
          {/* V91.3: 프로젝트 태그 마스터는 프로젝트 관리 내부 탭으로 이동 → 리다이렉트 */}
          <Route
            path="org-management/project-tags"
            element={<Navigate to="/org-management/projects" replace />}
          />
          <Route
            path="org-management/work-status"
            element={
              <OrgManagementAccessGuard>
                <OrgManagementRouteGuard>
                  <OrgWorkStatusPage />
                </OrgManagementRouteGuard>
              </OrgManagementAccessGuard>
            }
          />
          {/* V5.18: 조직 휴가 관리 (인사관리 휴가현황과 동일 UI, 조직 스코프 기반) */}
          {/* 기존 OrgLeavePage 플레이스홀더 삭제 → OrgLeaveManagementPage로 대체 */}
          <Route
            path="org-management/leave"
            element={
              <OrgManagementAccessGuard>
                <OrgLeaveManagementPage />
              </OrgManagementAccessGuard>
            }
          />
          {/* V66: 조직 휴가 캘린더는 /work/leave-calendar 로 이전됨. 구 경로 안전 redirect */}
          <Route
            path="org-management/leave-calendar"
            element={<Navigate to="/work/leave-calendar" replace />}
          />
          <Route
            path="org-management/interviews"
            element={
              <OrgManagementAccessGuard>
                <OrgManagementRouteGuard>
                  <OrgInterviewsPage />
                </OrgManagementRouteGuard>
              </OrgManagementAccessGuard>
            }
          />
          {/* V57: 조직원평가 */}
          <Route
            path="org-management/evaluations"
            element={
              <OrgManagementAccessGuard>
                <OrgManagementRouteGuard>
                  <OrgEvaluationsPage />
                </OrgManagementRouteGuard>
              </OrgManagementAccessGuard>
            }
          />
          {/* V60: 조직원전배 */}
          <Route
            path="org-management/transfer"
            element={
              <OrgManagementAccessGuard>
                <OrgTransferPage />
              </OrgManagementAccessGuard>
            }
          />
          {/* V68: 인력 관리 (인력투입 현황 + 유휴인력 관리) — role_hr 제외 */}
          <Route
            path="org-management/workforce"
            element={
              <OrgManagementAccessGuard>
                <WorkforceAccessGuard>
                  <OrgWorkforceManagementPage />
                </WorkforceAccessGuard>
              </OrgManagementAccessGuard>
            }
          />
          {/* V70.27: 인력 현황 (1차 뼈대)
              - 접근 판정 SSoT = 서버 `getMyWorkforceOverviewAccess` (Admin OR 설정 등록 사용자)
              - OrgManagementAccessGuard 는 적용하지 않는다: 조직관리 역할이 없는 '등록 사용자'도
                접근할 수 있어야 하므로, 접근 통제는 WorkforceOverviewAccessGuard 로 단일화한다.
              - 메뉴 숨김과 별개로 라우트 단계에서 차단된다 (직접 URL 입력 포함). */}
          <Route
            path="org-management/workforce-overview"
            element={
              <WorkforceOverviewAccessGuard>
                <OrgWorkforceOverviewPage />
              </WorkforceOverviewAccessGuard>
            }
          />
          {/* V55.30: 면담기록 작성 페이지 */}
          <Route
            path="org-management/interviews/:employeeId/new"
            element={
              <OrgManagementAccessGuard>
                <OrgManagementRouteGuard>
                  <InterviewWritePage />
                </OrgManagementRouteGuard>
              </OrgManagementAccessGuard>
            }
          />
          {/* V55.67: 면담 타입/템플릿 관리 페이지 */}
          <Route
            path="org-management/interviews/manage"
            element={
              <OrgManagementAccessGuard>
                <OrgManagementRouteGuard>
                  <InterviewManagementPage />
                </OrgManagementRouteGuard>
              </OrgManagementAccessGuard>
            }
          />

          {/* V60: MM관리 - MmAccessGuard 적용 */}
          {/*
            - /mm/master: role_exec + admin만 허용 (V113)
            - /mm/overview: role_manager, role_exec, admin만 허용 (TL 차단)
            - /mm/register: role_manager, role_exec, admin + TL 허용
          */}
          {/* V113: MM 마스터 설정 (role_exec + admin만 허용) */}
          <Route
            path="mm/master"
            element={
              <MmAccessGuard>
                <MMMasterPage />
              </MmAccessGuard>
            }
          />
          <Route
            path="mm/overview"
            element={
              <MmAccessGuard>
                <MMOverviewPage />
              </MmAccessGuard>
            }
          />
          <Route
            path="mm/register"
            element={
              <MmAccessGuard>
                <MMRegisterPage />
              </MmAccessGuard>
            }
          />

          {/* Admin (userType='admin'만 접근) */}
          {/* V234: ProtectedRoute 추가 — getEmployees(checkIsAdminLike) useEffect 마운트 호출 차단 */}
          <Route
            path="admin/roles"
            element={
              <ProtectedRoute path="/admin/roles">
                <AdminRolesPage />
              </ProtectedRoute>
            }
          />
          {/* V46: 승인요청 테스트 데이터 정리 (system_admin 전용) */}
          <Route path="admin/cleanup" element={<AdminCleanupPage />} />
          {/* V47.15: 휴가 데이터 테스트 유틸리티 (system_admin 전용) */}
          <Route path="admin/test-utilities" element={<AdminTestUtilitiesPage />} />
          {/* V82.7 Phase 1: IP 화이트리스트 관리 (system_admin 전용; 페이지 내부에서도 권한 재검증) */}
          <Route path="admin/ip-whitelist" element={<IpWhitelistPage />} />
          {/* V67: 알림 마스터 (system_admin 전용; 페이지 내부에서도 권한 재검증) */}
          <Route path="admin/notifications" element={<AdminNotificationSettingsPage />} />
          {/* V70.27: 인력 현황 관리 (Admin 전용; 페이지 내부에서도 'system.admin' 재검증, 서버도 재검증) */}
          <Route
            path="admin/workforce-overview"
            element={
              <ProtectedRoute path="/admin/workforce-overview">
                <AdminWorkforceOverviewSettingsPage />
              </ProtectedRoute>
            }
          />
          {/* V70 Tournament: 게임대회 설정 (system_admin 전용; 페이지/서버 재검증) */}
          <Route
            path="admin/tournament-settings"
            element={
              <ProtectedRoute path="/admin/tournament-settings">
                <TournamentAdminSettingsPage />
              </ProtectedRoute>
            }
          />
          {/* V70 Phase A: 참가신청 관리자 빌더 (system_admin 전용) */}
          <Route
            path="admin/tournament-applications"
            element={
              <ProtectedRoute path="/admin/tournament-applications">
                <TournamentApplyAdminPage />
              </ProtectedRoute>
            }
          />
          {/* V70 Tournament: 사용자 메뉴 (BE 설정 ON + 대상자 매칭 시만 접근) */}
          <Route
            path="tournament/dashboard"
            element={
              <TournamentAccessGuard>
                <TournamentDashboardPage />
              </TournamentAccessGuard>
            }
          />
          <Route
            path="tournament/apply"
            element={
              <TournamentAccessGuard>
                <TournamentApplyPage />
              </TournamentAccessGuard>
            }
          />
          <Route
            path="tournament/games"
            element={
              <TournamentAccessGuard>
                <TournamentGameCenterPage />
              </TournamentAccessGuard>
            }
          />
          {/* V70 Phase 2: 산성비 타자왕 게임 + 리더보드 */}
          <Route
            path="tournament/game/acid-rain"
            element={
              <TournamentAccessGuard>
                <OpenPeriodGuard
                  gameLabel="엔글 타자왕"
                  leaderboardPath="/tournament/leaderboard"
                  // V70-I — 연습 게임은 대회 기간 전·후에도 이용 (공식 도전은 게임 화면·서버에서 기간 제한 유지, OFF 는 차단)
                  allowOutsidePeriod
                  loadStatus={async () => {
                    const s = await acidRainApi.getSettings();
                    return { enabled: s.enabled, openStartAt: s.openStartAt ?? null, openEndAt: s.openEndAt ?? null };
                  }}
                >
                  <AcidRainGamePage />
                </OpenPeriodGuard>
              </TournamentAccessGuard>
            }
          />
          <Route
            path="tournament/leaderboard"
            element={
              <TournamentAccessGuard>
                <AcidRainLeaderboardPage />
              </TournamentAccessGuard>
            }
          />
          {/* V70 Phase 2: 엔글 마블 게임 + 리더보드 */}
          <Route
            path="tournament/game/marble"
            element={
              <TournamentAccessGuard>
                <OpenPeriodGuard
                  gameLabel="엔글 마블"
                  leaderboardPath="/tournament/leaderboard-marble"
                  loadStatus={async () => {
                    const s = await marbleApi.getSettings();
                    return { enabled: s.enabled, openStartAt: s.eventStartAt, openEndAt: s.eventEndAt };
                  }}
                >
                  <MarbleGamePage />
                </OpenPeriodGuard>
              </TournamentAccessGuard>
            }
          />
          <Route
            path="tournament/leaderboard-marble"
            element={
              <TournamentAccessGuard>
                <MarbleLeaderboardPage />
              </TournamentAccessGuard>
            }
          />
          {/* V70G6B — 엔글 오목 (실시간 1:1 대전) */}
          <Route
            path="tournament/game/omok"
            element={
              <TournamentAccessGuard>
                <OmokLobbyPage />
              </TournamentAccessGuard>
            }
          />
          <Route
            path="tournament/game/omok/room/:roomId"
            element={
              <TournamentAccessGuard>
                <OmokRoomPage />
              </TournamentAccessGuard>
            }
          />
          {/* V70 Phase C — 참가자 대진표 상세 */}
          <Route
            path="tournament/bracket/:bracketId"
            element={
              <TournamentAccessGuard>
                <BracketDetailPage />
              </TournamentAccessGuard>
            }
          />
          {/* V70 엔글 룰렛 — 다중 경품 추첨 플랫폼 (QA 전용, system_admin 가드).
                4종 추첨 게임 (서바이벌 / 잭팟 / 룰렛 / 미스터리박스) + 공정 RNG + ERP 직원 필터 연동.
                프로덕션 접근은 TournamentAccessGuard로 보호 — 로그인 + 대상자 매칭 필요. */}
          <Route
            path="tournament/roulette"
            element={
              <TournamentAccessGuard>
                <Suspense fallback={<div style={{ padding: 24 }}>로딩 중…</div>}>
                  <RouletteMainPage />
                </Suspense>
              </TournamentAccessGuard>
            }
          />
          {/* 레거시 V1 Demo (메뉴 미노출, 직접 URL 접근용으로 보존) */}
          <Route
            path="tournament/roulette/demo"
            element={
              <TournamentAccessGuard>
                <Suspense fallback={<div style={{ padding: 24 }}>로딩 중…</div>}>
                  <RouletteDemoPage />
                </Suspense>
              </TournamentAccessGuard>
            }
          />
          {/* V70 엔글 룰렛 V2P — Static Asset Copy (QA 전용, system_admin 가드).
                public/game-v2/* 로 redirect — React 번들 밖, SPA 라우팅 안에서 external navigation. */}
          <Route
            path="tournament/roulette-v2"
            element={
              <TournamentAccessGuard>
                <Suspense fallback={<div style={{ padding: 24 }}>로딩 중…</div>}>
                  <RouletteV2Redirect />
                </Suspense>
              </TournamentAccessGuard>
            }
          />
          {/* V70 엔글 룰렛 V3 — 1차 데모 (메뉴 미노출, URL 직접 접근, system_admin 가드). */}
          <Route
            path="tournament/roulette-v3"
            element={
              <TournamentAccessGuard>
                <Suspense fallback={<div style={{ padding: 24 }}>로딩 중…</div>}>
                  <RouletteV3Redirect />
                </Suspense>
              </TournamentAccessGuard>
            }
          />
        </Route>

        {/* V55.23: 404 - 공지사항으로 리다이렉트 */}
        <Route path="*" element={<Navigate to="/announcements" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;

/**
 * ============================================
 * TODO: Route Guard (라우트 권한 체크) - 향후 구현
 * ============================================
 *
 * 현재는 URL을 직접 입력하면 권한 없이도 페이지에 접근할 수 있습니다.
 * 향후 RBAC 시스템을 연동하여 권한이 없는 사용자가 접근하면 차단해야 합니다.
 *
 * **구현 방법 1: ProtectedRoute 컴포넌트 확장**
 *
 * 현재 ProtectedRoute는 로그인 여부만 확인합니다.
 * 이를 확장하여 권한도 함께 확인하도록 수정할 수 있습니다.
 *
 * ```tsx
 * // src/shared/components/ProtectedRoute.tsx
 *
 * interface ProtectedRouteProps {
 *   children: React.ReactElement;
 *   requiredPermission?: PermissionKey; // 필요한 권한
 *   requiredRoute?: string; // 필요한 라우트 (canAccessRoute로 체크)
 * }
 *
 * export function ProtectedRoute({
 *   children,
 *   requiredPermission,
 *   requiredRoute
 * }: ProtectedRouteProps) {
 *   const authContext = useAuthContext();
 *   const navigate = useNavigate();
 *
 *   // 로그인 체크 (기존 로직)
 *   if (!authContext.isLoaded) {
 *     return <div>Loading...</div>;
 *   }
 *
 *   if (!authContext.userId) {
 *     return <Navigate to="/login" replace />;
 *   }
 *
 *   // 권한 체크 (신규 로직)
 *   if (requiredPermission && !hasPermission(authContext, requiredPermission)) {
 *     // 권한 없음 페이지로 이동
 *     return <Navigate to="/403" replace />;
 *   }
 *
 *   if (requiredRoute && !canAccessRoute(authContext, requiredRoute)) {
 *     // 권한 없음 페이지로 이동
 *     return <Navigate to="/403" replace />;
 *   }
 *
 *   return children;
 * }
 * ```
 *
 * **사용 예시:**
 *
 * ```tsx
 * // 특정 권한이 필요한 라우트
 * <Route
 *   path="hr/employees"
 *   element={
 *     <ProtectedRoute requiredPermission="hr.manage">
 *       <HrEmployeesPage />
 *     </ProtectedRoute>
 *   }
 * />
 *
 * // 또는 라우트 경로로 자동 체크
 * <Route
 *   path="hr/employees"
 *   element={
 *     <ProtectedRoute requiredRoute="/hr/employees">
 *       <HrEmployeesPage />
 *     </ProtectedRoute>
 *   }
 * />
 * ```
 *
 * **구현 방법 2: 별도 RouteGuard 컴포넌트 생성**
 *
 * ```tsx
 * // src/shared/components/RouteGuard.tsx
 *
 * interface RouteGuardProps {
 *   children: React.ReactElement;
 *   route: string;
 * }
 *
 * export function RouteGuard({ children, route }: RouteGuardProps) {
 *   const authContext = useAuthContext();
 *
 *   // 권한 체크
 *   if (!canAccessRoute(authContext, route)) {
 *     return (
 *       <div style={{ padding: '40px', textAlign: 'center' }}>
 *         <h1>🔒 접근 권한이 없습니다</h1>
 *         <p>이 페이지에 접근할 권한이 없습니다.</p>
 *         <p>필요한 권한: {ROUTE_PERMISSIONS[route]}</p>
 *         <Link to="/dashboard">대시보드로 이동</Link>
 *       </div>
 *     );
 *   }
 *
 *   return children;
 * }
 * ```
 *
 * **사용 예시:**
 *
 * ```tsx
 * // App.tsx에서 사용
 * <Route
 *   path="hr/employees"
 *   element={
 *     <RouteGuard route="/hr/employees">
 *       <HrEmployeesPage />
 *     </RouteGuard>
 *   }
 * />
 * ```
 *
 * **구현 방법 3: 403 Forbidden 페이지 생성**
 *
 * ```tsx
 * // src/pages/ForbiddenPage.tsx
 *
 * export function ForbiddenPage() {
 *   const location = useLocation();
 *   const navigate = useNavigate();
 *   const authContext = useAuthContext();
 *
 *   const attemptedRoute = location.state?.from || '/';
 *   const requiredPermission = ROUTE_PERMISSIONS[attemptedRoute];
 *
 *   return (
 *     <div style={{ padding: '40px', textAlign: 'center' }}>
 *       <h1 style={{ fontSize: '48px' }}>🔒</h1>
 *       <h2>접근 권한이 없습니다</h2>
 *       <p>요청하신 페이지에 접근할 권한이 없습니다.</p>
 *       <div style={{ marginTop: '20px', padding: '20px', background: '#f5f5f5', borderRadius: '8px' }}>
 *         <p><strong>시도한 페이지:</strong> {attemptedRoute}</p>
 *         <p><strong>필요한 권한:</strong> {Array.isArray(requiredPermission) ? requiredPermission.join(', ') : requiredPermission}</p>
 *         <p><strong>현재 역할:</strong> {getUserRoles(authContext).join(', ')}</p>
 *       </div>
 *       <div style={{ marginTop: '30px' }}>
 *         <button onClick={() => navigate(-1)}>뒤로 가기</button>
 *         <button onClick={() => navigate('/dashboard')}>대시보드로 이동</button>
 *       </div>
 *     </div>
 *   );
 * }
 * ```
 *
 * **App.tsx에 403 라우트 추가:**
 *
 * ```tsx
 * // 403 페이지 추가
 * <Route path="/403" element={<ForbiddenPage />} />
 * ```
 *
 * **예시: 권한별 접근 제어**
 *
 * - **일반 직원 (general)**:
 *   - 접근 가능: /dashboard, /notice, /organization, /work/my-*
 *   - 접근 불가: /hr/*, /team/*, /mm/*, /work/approvals
 *
 * - **TL (tl)**:
 *   - 일반 직원 권한 + /mm/overview, /mm/register
 *
 * - **조직장 (org_manager)**:
 *   - TL 권한 + /team/*, /work/approvals, /mm/manage
 *
 * - **HR (hr)**:
 *   - 거의 모든 페이지 접근 가능 (시스템 관리 제외)
 *
 * - **Super Admin (super_admin)**:
 *   - 모든 페이지 접근 가능
 *
 * **DEMO 모드 테스트:**
 *
 * ```tsx
 * // 일반 직원으로 /hr/employees 접근 시도
 * // → 403 페이지로 리다이렉트
 * // → "hr.manage 권한이 필요합니다" 메시지 표시
 *
 * // HR로 /hr/employees 접근 시도
 * // → 정상 접근
 * ```
 */
